import { useCallback, useEffect, useRef } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import * as server_api from '../../../../../../common/src/server_api.ts';
import { isValidEmail } from '../../../../../../common/src/util.ts';
import { alert } from '../../../../util/confirm.tsx';
import { CloseButton } from '../../../../util/shared.tsx';
import { Button } from '../../../onboarding/button.tsx';
import { Message, TextInput } from '../../../onboarding/common.tsx';

const DeleteHouseholdDialog: ConfirmDialog<any, any> = ({ proceed, show }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const householdEmailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
      householdEmailInputRef.current?.focus();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);

  const onClose = useCallback(() => {
    proceed({});
  }, [proceed]);

  const onClickDialog = useCallback(
    (e: any) => {
      const didClickBackdrop = e.currentTarget === e.target;
      if (didClickBackdrop) {
        onClose();
      }
    },
    [onClose],
  );

  const onDelete = useCallback(async () => {
    const householdEmail = householdEmailInputRef.current?.value;
    if (!isValidEmail(householdEmail)) {
      await alert({ confirmation: <span>Invalid email address</span> });
      return;
    }
    try {
      await server_api.deleteHousehold({ householdEmail });
    } catch (e: any) {
      await alert({ confirmation: <span>Error: {e.message}</span> });
      return;
    }
    await alert({ confirmation: <span>Household deleted</span> });
    onClose();
  }, [onClose]);

  const onKeyDown = useCallback(
    async (evt: any) => {
      if (evt.code === 'Enter') {
        await onDelete();
      }
    },
    [onDelete],
  );

  return (
    <Dialog ref={dialogRef} onClick={onClickDialog} onKeyDown={onKeyDown}>
      <VerticalStacker>
        <CloseButton onClick={onClose} isCloseToTop={true} />
        <Message $widthInChars={21} $noPadding={true}>
          What is the household email?
        </Message>
        <TextInput type="text" ref={householdEmailInputRef} />
        <Button onClick={onDelete} isDangerous={true}>
          DELETE
        </Button>
      </VerticalStacker>
    </Dialog>
  );
};

export const VerticalStacker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;
`;

const Dialog = styled.dialog`
  border: none;
  border-radius: 20px;
  position: fixed;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
  margin: auto;
  background: var(--color-background);
  padding: 75px 30px;
`;

export const showDeleteHouseholdDialog = createConfirmation(confirmable(DeleteHouseholdDialog));
